import React, { useCallback, useEffect, useState } from "react";
import { Link, Redirect, useHistory, useLocation } from "react-router-dom";
import axios from "axios";
import SessionManager from "./Utils/SessionManager";
import myToast from "./Utils/MyToast";
import COLORS from "./resources/colors/COLORS";

const ACCENT = COLORS.primary_color_shade6;
const ACCENT_DARK = COLORS.primary_color_shade8;
const ACCENT_SOFT = COLORS.primary_color_shade0;

const LOGIN_TYPES = {
  ADMIN: process.env.REACT_APP_LOGIN_TYPE_ADMIN,
  CLERK: process.env.REACT_APP_LOGIN_TYPE_CLERK,
};

const API_BASE = () =>
  process.env.REACT_APP_SERVER_NAME + process.env.REACT_APP_TARGET_DIR;

//Created By Cursor
function SchoolRegistrationSuccess() {
  const history = useHistory();
  const location = useLocation();
  const staffId = location.state?.staff_id || "";

  const [staff, setStaff] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loggingIn, setLoggingIn] = useState(false);

  useEffect(() => {
    const id = "eduok-login-fonts";
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Outfit:wght@500;600;700;800&family=Source+Sans+3:wght@400;500;600;700&display=swap";
    document.head.appendChild(link);
  }, []);

  //Created By Cursor
  const loadStaffDetails = useCallback(async () => {
    if (!staffId) return;
    setLoading(true);
    try {
      const response = await axios.get(API_BASE() + "getStaffDetails.php?", {
        params: { staff_id: staffId },
      });
      if (
        response?.data?.api_status === "OK" &&
        Array.isArray(response.data.data) &&
        response.data.data[0]
      ) {
        setStaff(response.data.data[0]);
      } else {
        myToast.notify_warning(
          response?.data?.api_remark || "Could not load login details"
        );
      }
    } catch (error) {
      console.error("SchoolRegistrationSuccess loadStaff:", error);
      myToast.notify_warning("Failed to load staff details.");
    } finally {
      setLoading(false);
    }
  }, [staffId]);

  useEffect(() => {
    loadStaffDetails();
  }, [loadStaffDetails]);

  //Created By Cursor
  const handleGetLogin = useCallback(async () => {
    if (!staff?.staff_id || !staff?.password) {
      myToast.notify_warning("Login details not available yet.");
      return;
    }

    setLoggingIn(true);
    try {
      const response = await axios.get(API_BASE() + "staffLogin.php?", {
        params: {
          user_id: staff.staff_id,
          password: staff.password,
          system_code: process.env.REACT_APP_SYSTEM_CODE,
          version_code: "1.1",
        },
      });

      if (response?.data?.api_status === "OK") {
        const { token, staff: staffList } = response.data;
        const staffData = staffList?.[0];
        if (!token || !staffData) {
          myToast.notify_warning("Invalid login response.");
          history.push("/Login");
          return;
        }

        localStorage.setItem("token", token);
        SessionManager.saveSchoolCode(staffData.school_code);
        SessionManager.createStaffLoginSession(
          staffData.staff_id,
          staffData.staff_name,
          staffData.staff_phone,
          staffData.staff_email,
          staffData.password,
          staffData.staff_role,
          staffData.login_type,
          staffData.staff_image_path
        );

        if (
          staffData.login_type === LOGIN_TYPES.ADMIN ||
          staffData.login_type === LOGIN_TYPES.CLERK
        ) {
          history.replace("/AdminMain");
        } else {
          myToast.notify_warning(
            "Access Not Granted. Please use the mobile application."
          );
          history.push("/Login");
        }
      } else {
        myToast.notify_warning(
          response?.data?.api_remark || "Login failed. Please sign in manually."
        );
        history.push("/Login");
      }
    } catch (error) {
      console.error("SchoolRegistrationSuccess getLogin:", error);
      myToast.notify_warning("Login failed. Please sign in from the login page.");
      history.push("/Login");
    } finally {
      setLoggingIn(false);
    }
  }, [staff, history]);

  if (!staffId) {
    return <Redirect to="/Register" />;
  }

  const infoRows = staff
    ? [
        { label: "School Code", value: staff.school_code },
        { label: "Admin", value: staff.staff_name },
        { label: "Login Type", value: staff.login_type },
        { label: "User Id", value: staff.staff_id },
        { label: "Password", value: staff.password },
      ]
    : [];

  return (
    <div
      style={{
        minHeight: "100vh",
        fontFamily: '"Source Sans 3", sans-serif',
        background: `linear-gradient(145deg, ${ACCENT_SOFT} 0%, #ffffff 42%, #e0f2fe 100%)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "32px 16px",
        boxSizing: "border-box",
      }}
    >
      <style>{`
        .eduok-success-btn:hover:not(:disabled) {
          background: ${ACCENT_DARK} !important;
          transform: translateY(-1px);
        }
      `}</style>

      <div
        style={{
          width: 480,
          maxWidth: "100%",
          padding: "34px 30px 28px",
          borderRadius: 18,
          background: "rgba(255,255,255,0.96)",
          border: "1px solid #d7e6f4",
          boxShadow: "0 16px 40px rgba(1, 87, 155, 0.12)",
          textAlign: "center",
        }}
      >
        <div
          style={{
            width: 72,
            height: 72,
            margin: "0 auto 16px",
            borderRadius: 20,
            background: ACCENT_SOFT,
            border: `2px solid ${ACCENT}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <i
            className="fa fa-check"
            style={{ fontSize: 32, color: ACCENT_DARK }}
            aria-hidden
          />
        </div>

        <div
          style={{
            fontFamily: '"Outfit", sans-serif',
            fontSize: 26,
            fontWeight: 800,
            color: ACCENT_DARK,
            marginBottom: 6,
          }}
        >
          Edu<span style={{ color: ACCENT }}>OK</span>
        </div>

        <p
          style={{
            margin: "0 0 8px",
            fontFamily: '"Outfit", sans-serif',
            fontSize: 22,
            fontWeight: 700,
            color: "#0f172a",
          }}
        >
          Registration successful
        </p>
        <p style={{ margin: "0 0 22px", fontSize: 14, color: "#64748b" }}>
          Your school is ready. Save these admin credentials and continue.
        </p>

        {loading ? (
          <div style={{ padding: "28px 0", color: "#64748b" }}>
            <i className="fa fa-spinner fa-spin" aria-hidden /> Loading
            credentials…
          </div>
        ) : (
          <div
            style={{
              textAlign: "left",
              border: "1px solid #d7e6f4",
              borderRadius: 14,
              overflow: "hidden",
              marginBottom: 22,
            }}
          >
            {infoRows.map((row, index) => (
              <div
                key={row.label}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 12,
                  padding: "12px 14px",
                  borderBottom:
                    index === infoRows.length - 1
                      ? "none"
                      : "1px solid #e8eef5",
                  background: index % 2 === 0 ? "#fff" : "#f8fbff",
                }}
              >
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    color: "#64748b",
                  }}
                >
                  {row.label}
                </span>
                <span
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: "#0f172a",
                    wordBreak: "break-all",
                    textAlign: "right",
                  }}
                >
                  {row.value || "—"}
                </span>
              </div>
            ))}
          </div>
        )}

        <button
          type="button"
          className="eduok-success-btn"
          onClick={handleGetLogin}
          disabled={loading || loggingIn || !staff}
          style={{
            width: "100%",
            height: 50,
            border: "none",
            borderRadius: 10,
            background: ACCENT,
            color: "#fff",
            fontFamily: '"Outfit", sans-serif',
            fontSize: 16,
            fontWeight: 700,
            cursor: loading || loggingIn || !staff ? "not-allowed" : "pointer",
            opacity: loading || loggingIn || !staff ? 0.75 : 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 10,
            transition: "background 0.2s ease, transform 0.2s ease",
            marginBottom: 14,
          }}
        >
          {loggingIn ? (
            <>
              <i className="fa fa-spinner fa-spin" aria-hidden />
              Signing in…
            </>
          ) : (
            <>
              Get Login
              <i className="fa fa-sign-in" aria-hidden />
            </>
          )}
        </button>

        <Link
          to="/Login"
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: ACCENT,
            textDecoration: "none",
          }}
        >
          Go to Login instead
        </Link>
      </div>
    </div>
  );
}

export default SchoolRegistrationSuccess;
